import { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "../ChangeDialogs.css";

import ModalHeaderWithActions from "../../../../UI/ModalHeaderWithActions";

import ProfileDataNode from "../ProfileDataNode";
import ProfileEditNode from "../ProfileEditNode";

import { useDispatch } from "react-redux";
import { toastError, toastSuccess } from "../../../../../components/custom-toasts";
import { isEmailValid, isPhoneValid } from "../../../../../components/validation";
import { AppDispatch, AppState } from "../../../../../store/configureStore";
import Gender from "../../../../../util/config/gender";
import RelatedPerson from "../../../../../entities/relatedPersons/RelatedPerson";
import { saveContactPerson } from "../../../../../store/slices/relatedPersons";
import { useSelector } from "react-redux";

interface ContactPerson {
    Id: number;
    FirstName?: string;
    LastName?: string;
    Name: string;
    Email: string;
    Phone: string;
}

interface Props {
    currentItem: RelatedPerson | undefined;
    show: boolean;
    isInEditMode: boolean;
    onClose: () => void;
    onSubmit: (data: RelatedPerson) => void;
}

// Define field validation structure
interface FieldValidation {
    valid: boolean;
    touched: boolean;
    errorMessage: string;
}

// Define validation state structure
type ValidationState = {
    [key in keyof ContactPerson]?: FieldValidation;
};

const ContactPersonModal = (props: Props) => {

    const { t } = useTranslation("parts");
    const dispatch = useDispatch<AppDispatch>();

    const initialValue: ContactPerson = {
        Id: 0,
        FirstName: "",
        LastName: "",
        Name: "",
        Phone: "",
        Email: ""
    };

    const [editItem, setEditItem] = useState<ContactPerson>(props.currentItem || initialValue);
    const { show, isInEditMode, onClose, onSubmit } = props;

    const validateField = (field: keyof ContactPerson, value: any, dontTouch: boolean = false): FieldValidation => {
        let valid = true;
        let errorMessage = "";//isPhoneValid

        switch (field) {
            case "Phone":
                if (!value || typeof value === "string" || value.trim() === "") {
                    valid = true;
                    errorMessage = "";
                } else {
                    valid = isPhoneValid(value);
                    errorMessage = valid ? "" : t("common:form.errors.invalidPhone");
                }
                break;
            case "FirstName":
            case "LastName":
                valid = (typeof value === "string") && value.trim().length >= 2;
                errorMessage = valid ? "" : t("common:form.errors.required");
                break;
            case "Email":
                if (!value || typeof value === "string" || value.trim() === "") {
                    valid = true;
                    errorMessage = "";
                } else {
                    valid = isEmailValid(value);
                    errorMessage = valid ? "" : t("parts:contacts.errors.email");
                }
                break;
            default:
                valid = true;
                errorMessage = "";
        }

        return { valid, touched: !dontTouch, errorMessage };
    };
    const workerId  = useSelector( (state: AppState) => state.users.auth.data?.WorkerId);

    // Initialize validation state with all required fields
    const [validation, setValidation] = useState<ValidationState>(() => {
        const input = editItem;
        const initialValidation: ValidationState = {};
        for (const field of Object.keys(input) as Array<keyof ContactPerson>) {
            initialValidation[field] = validateField(field, input[field], true);
        }
        return initialValidation;
    });

    useEffect(() => {

        if (props.show) {

            const input = props.currentItem || initialValue;
            setEditItem(input);

            const newValidation = { ...validation };
            for (const field of Object.keys(input) as Array<keyof ContactPerson>) {
                newValidation[field] = validateField(field, input[field], true);
            }

            setValidation(newValidation);
        }
    }, [props.show]);

    // Check if all required fields are valid
    const canSubmit = useMemo(() => {
        const hasPhone = !!editItem.Phone && editItem.Phone.trim() !== "";
        const hasEmail = !!editItem.Email && editItem.Email.trim() !== "";

        return validation.FirstName?.valid === true &&
            validation.LastName?.valid === true &&
            validation.Email?.valid === true &&
            validation.Phone?.valid === true &&
            (hasPhone || hasEmail); // At least one contact method must be provided
    }, [validation, editItem.Phone, editItem.Email]);

    const handleSubmit = () => {
        if (!canSubmit) {
            return;
        }

        const newItem: RelatedPerson = {
            ...(props.currentItem || {} as RelatedPerson),
            ...editItem
        };

        newItem.Gender = Gender.Unknown;
       // newItem.RelationshipType = RelationshipType.Other;
        newItem.IsDependent = false;
        newItem.IsEmergencyContact = true;
        newItem.Name = `${newItem.FirstName} ${newItem.LastName}`;

        try {

            dispatch(saveContactPerson({
                WorkerId: workerId ?? 0,
                Id: newItem.Id ?? undefined,
                IsDependent: newItem.IsDependent,
                FirstName: newItem.FirstName!,
                RelationshipType: 0,
                LastName: newItem.LastName!,
                Email: newItem.Email,
                Phone: newItem.Phone,
                BirthDate: newItem.BirthDate,
                Gender: newItem.Gender!,
                IdentificationNumber: newItem.IdentificationNumber,
                IsEmergencyContact: newItem.IsEmergencyContact,
                AdditionalData: newItem.AdditionalData
            }));

            onSubmit(newItem);
            toastSuccess({ message: t(`common:posted`) });
            return true;
        } catch (err) {
            toastError({ message: t(`common:postError`) });
            console.error(err);
        }

    };

    const handleChange = (field: keyof ContactPerson, value: any) => {
        setEditItem((prev) => ({
            ...prev,
            [field]: value
        }));

        setValidation(prev => ({
            ...prev,
            [field]: validateField(field, value)
        }));
    };

    const getErrorMessage = (field: keyof ContactPerson): string => {
        const fieldValidation = validation[field];

        // Only show error if field has been touched
        if (!fieldValidation || !fieldValidation.touched) {
            return "";
        }

        return fieldValidation.errorMessage;
    };


    return show && (
        <Modal show={show} onHide={onClose} className="change-modal">
            <ModalHeaderWithActions
                title={t("parts:relatedContact.title")}
                onClose={onClose}
                onSubmit={handleSubmit}
                canSubmit={canSubmit}
                isInEditMode={isInEditMode}
            />
            <Modal.Body>
                {isInEditMode ? (
                    <Form>
                        <ProfileEditNode
                            mandatory={true}
                            controlId="formFirstName"
                            label={t("pages:profile.firstName")}
                            value={editItem.FirstName || ""}
                            error={getErrorMessage("FirstName")}
                            placeholder={t("common:form.placeholders.enterData")}
                            onChange={(value) => { handleChange('FirstName', value) }}
                        />
                        <ProfileEditNode
                            mandatory={true}
                            controlId="formLastName"
                            label={t("pages:profile.lastName")}
                            value={editItem.LastName || ""}
                            error={getErrorMessage("LastName")}
                            placeholder={t("common:form.placeholders.enterData")}
                            onChange={(value) => { handleChange('LastName', value) }}
                        />
                        <ProfileEditNode
                            mandatory={!editItem.Email || editItem.Email.trim() === ""}
                            controlId="formPhone"
                            label={t("parts:relatedContact.columns.phone")}
                            value={editItem.Phone || ""}
                            error={getErrorMessage("Phone")}
                            placeholder={t("common:form.placeholders.enterData")}
                            onChange={(value) => { handleChange('Phone', value) }}
                        />
                        <ProfileEditNode
                            mandatory={!editItem.Phone || editItem.Phone.trim() === ""}
                            controlId="formEmail"
                            label={t("pages:profile.email")}
                            value={editItem.Email || ""}
                            error={getErrorMessage("Email")}
                            placeholder={t("common:form.placeholders.enterData")}
                            onChange={(value) => { handleChange('Email', value) }}
                        />

                    </Form>
                ) : (
                    <div>
                        <ProfileDataNode label={t("pages:profile.firstName")}>{editItem.FirstName || "-"}</ProfileDataNode>
                        <ProfileDataNode label={t("pages:profile.lastName")}>{editItem.LastName || "-"}</ProfileDataNode>
                        <ProfileDataNode label={t("pages:profile.phone")}>{editItem.Phone || "-"}</ProfileDataNode>
                        <ProfileDataNode label={t("pages:profile.email")}>{editItem.Email || "-"}</ProfileDataNode>

                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};
export default ContactPersonModal;
